import { useEffect, useRef, useState } from "react";
import { api, formatApiError, resolveBackendAssetUrl } from "../../lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Image as ImageIcon } from "lucide-react";

const SLOTS = [
  { key: "logo", label: "Logo principal", desc: "Marca de la cabecera pública." },
  { key: "hero", label: "Hero (portada)", desc: "Imagen de fondo grande de la home." },
  { key: "mimosa", label: "Mimosa de la Feria", desc: "Bloque del producto estrella." },
  { key: "aperitivo", label: "Aperitivo", desc: "Sección de experiencias." },
  { key: "comida", label: "Comida", desc: "Sección de experiencias." },
  { key: "tardeo", label: "Tardeo", desc: "Sección de experiencias." },
  { key: "noche", label: "Noche", desc: "Sección de experiencias." },
];

export default function AdminMedia() {
  const [media, setMedia] = useState({});
  const [uploading, setUploading] = useState(null);

  const load = () =>
    api.get("/content/media", { params: { _t: Date.now() } }).then((r) => setMedia(r.data.data || {}));

  useEffect(() => {
    load();
  }, []);

  const handleUpload = async (slot, file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) return toast.error("Máximo 6 MB");
    setUploading(slot);
    const fd = new FormData();
    fd.append("slot", slot);
    fd.append("file", file);
    try {
      await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      toast.success("Imagen actualizada");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(null);
    }
  };

  const remove = async (slot) => {
    if (!confirm("¿Eliminar esta imagen? Se volverá a la imagen por defecto.")) return;
    try {
      await api.delete(`/media/${slot}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-media">
      <header>
        <h1 className="font-display font-black text-3xl">Imágenes de la Home</h1>
        <p className="text-muted-foreground">Sube fotos reales del local. JPG, PNG, WEBP o GIF · máx 6 MB.</p>
      </header>

      <div className="grid sm:grid-cols-2 gap-4">
        {SLOTS.map((s) => {
          const url = resolveBackendAssetUrl(media[s.key], media._updated_at);
          const isUploading = uploading === s.key;
          const fitClass = s.key === "logo" ? "object-contain p-4" : "object-cover";
          return (
            <div key={s.key} className="bg-white rounded-2xl border border-border overflow-hidden" data-testid={`media-slot-${s.key}`}>
              <div className="relative aspect-[16/10] bg-accent">
                {url ? (
                  <img src={url} alt={s.label} className={`absolute inset-0 w-full h-full ${fitClass}`} />
                ) : (
                  <div className="absolute inset-0 grid place-items-center text-muted-foreground">
                    <div className="text-center">
                      <ImageIcon size={32} className="mx-auto opacity-60" />
                      <div className="text-xs mt-2 uppercase tracking-widest">Imagen por defecto</div>
                    </div>
                  </div>
                )}
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <div className="font-display font-bold">{s.label}</div>
                  <div className="text-sm text-muted-foreground">{s.desc}</div>
                </div>
                <div className="flex gap-2">
                  <UploadButton slotKey={s.key} busy={isUploading} onFile={(f) => handleUpload(s.key, f)} />
                  {url && (
                    <button
                      onClick={() => remove(s.key)}
                      className="inline-flex items-center gap-1 bg-destructive/10 text-destructive border border-destructive/30 px-3 py-2 rounded-full text-sm font-medium hover:bg-destructive hover:text-destructive-foreground transition"
                      data-testid={`media-delete-${s.key}`}
                    >
                      <Trash2 size={14} /> Quitar
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UploadButton({ slotKey, busy, onFile }) {
  const inputRef = useRef(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          if (inputRef.current) inputRef.current.value = "";
        }}
        data-testid={`media-input-${slotKey}`}
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="inline-flex items-center gap-1 bg-primary text-primary-foreground px-4 py-2 rounded-full text-sm font-bold hover:brightness-110 disabled:opacity-60 transition"
        data-testid={`media-upload-${slotKey}`}
      >
        <Upload size={14} /> {busy ? "Subiendo..." : "Subir imagen"}
      </button>
    </>
  );
}
